import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Printer, History, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { NativeSelect, DemoBadge } from "@/components/common";
import { ASSET_TONE, useAssetMeta, AssetFormDialog, due30 } from "@/pages/Mezzi";
import { AuthImage } from "@/components/AuthImage";
import { AssetGallery } from "@/components/AssetGallery";

const CoverPhoto = ({ a, onDone }) => {
  const ref = useRef();
  const up = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("cover", "true");
    try {
      onDone((await api.post(`/assets/${a.id}/photos`, fd)).data);
      toast.success("Foto principale aggiornata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-3 flex flex-wrap items-end gap-3" data-testid="asset-cover">
      <AuthImage fileId={a.cover_photo_id} alt={a.code} className="h-40 w-full max-w-sm rounded-xl" testid="asset-cover-photo" />
      {a.can.photos && <>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => up(e.target.files[0])} data-testid="asset-cover-input" />
        <Button variant="outline" size="sm" onClick={() => ref.current.click()} className="rounded-xl" data-testid="asset-cover-upload">{a.cover_photo_id ? "Cambia foto principale" : "Aggiungi foto principale"}</Button>
      </>}
    </div>
  );
};
import { fmtD, fmtDT } from "@/lib/format";

const ACTS = {
  checklist_inizio: "Checklist inizio utilizzo", checklist_fine: "Checklist fine utilizzo", assign: "Consegna a dipendente", return: "Restituzione",
  report_fault: "Segnala guasto / danno", confirm_possession: "Conferma possesso", request_possession: "Richiedi conferma possesso",
  start_maintenance: "Avvia manutenzione", complete_maintenance: "Registra manutenzione eseguita", reenable: "Riabilita all'uso", retire: "Fuori servizio",
};
const canKey = (a) => (a.startsWith("checklist") ? "checklist" : a);

const ActDialog = ({ asset, act, meta, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [items, setItems] = useState(asset.template.map((l) => ({ label: l, ok: true })));
  const [outcome, setOutcome] = useState("regolare");
  const [blocking, setBlocking] = useState(false);
  const [people, setPeople] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [nextDue, setNextDue] = useState("");
  const [state, setState] = useState("integro");
  useEffect(() => {
    if (act === "assign") api.get("/users", { params: { commessa_id: asset.commessa_id } }).then((r) => setPeople(r.data.filter((u) => u.active)));
  }, [act, asset.commessa_id]);
  const go = async () => {
    const action = act.startsWith("checklist") ? "checklist" : act;
    const phase = act === "checklist_inizio" ? "inizio" : act === "checklist_fine" ? "fine" : null;
    try {
      const { data } = await api.post(`/assets/${asset.id}/action`, { action, note, phase, items, outcome: phase === "fine" ? outcome : null, blocking, assignee_id: assignee, next_due: nextDue ? new Date(nextDue).toISOString() : null, state });
      toast.success(`${ACTS[act]}: registrato`);
      onDone(data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl" data-testid={`asset-action-dialog-${act}`}>
        <DialogHeader><DialogTitle>{ACTS[act]} · {asset.code}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {act.startsWith("checklist") && items.map((it, i) => (
            <div key={it.label} className="flex items-center gap-2 rounded-xl border p-2">
              <span className="flex-1 text-sm font-semibold">{it.label}</span>
              {[true, false].map((v) => (
                <button key={String(v)} onClick={() => setItems(items.map((x, j) => (j === i ? { ...x, ok: v } : x)))} data-testid={`chk-${i}-${v ? "ok" : "ko"}`}
                  className={`min-h-[44px] min-w-[64px] rounded-xl px-3 text-sm font-bold ${it.ok === v ? (v ? "bg-emerald-600 text-white" : "bg-red-600 text-white") : "bg-muted"}`}>{v ? "OK" : "KO"}</button>
              ))}
            </div>
          ))}
          {act === "checklist_fine" && <NativeSelect className="w-full" testid="chk-outcome" value={outcome} onChange={setOutcome}>{Object.entries(meta.end_outcomes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect>}
          {act === "assign" && <NativeSelect className="w-full" testid="asset-assignee-select" value={assignee} onChange={setAssignee}><option value="">Dipendente…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</NativeSelect>}
          {act === "report_fault" && <label className="flex items-center gap-2 rounded-xl border p-3 text-sm font-semibold"><input type="checkbox" checked={blocking} onChange={(e) => setBlocking(e.target.checked)} data-testid="fault-blocking" />Guasto bloccante (il mezzo diventa non utilizzabile)</label>}
          {act === "complete_maintenance" && <div className="space-y-1"><Label>Prossima manutenzione</Label><Input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} data-testid="maint-next-due" /></div>}
          {act === "confirm_possession" && <NativeSelect className="w-full" testid="possession-state" value={state} onChange={setState}><option value="integro">In mio possesso, integro</option><option value="danneggiato">In mio possesso, danneggiato</option><option value="restituzione">Chiedo la restituzione</option></NativeSelect>}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note / descrizione" data-testid="asset-action-note" />
        </div>
        <DialogFooter><Button onClick={go} className="rounded-xl font-bold" data-testid="asset-action-confirm">Conferma</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function MezzoDetail() {
  const { id } = useParams();
  const meta = useAssetMeta();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [act, setAct] = useState(null);
  const [editing, setEditing] = useState(false);
  const qr = useRef();
  const load = useCallback(() => {
    api.get(`/assets/${id}`).then((r) => setA(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  const resolve = async (f) => {
    const note = window.prompt("Descrivi la risoluzione del guasto:");
    if (!note) return;
    try {
      setA((await api.post(`/assets/${id}/action`, { action: "resolve_fault", fault_id: f.id, note })).data);
      toast.success("Guasto risolto");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const printQr = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><body style="font-family:sans-serif;text-align:center">${qr.current.innerHTML}<h2>${a.code}</h2><p>${a.brand} ${a.model}</p></body></html>`);
    w.print();
  };
  if (err) return <div className="card-soft p-8 text-center font-bold text-red-600" data-testid="asset-error">{err}</div>;
  if (!a || !meta) return <p className="text-muted-foreground">Caricamento…</p>;
  const avail = Object.keys(ACTS).filter((k) => a.can[canKey(k)]);
  const primary = avail.filter((k) => k.startsWith("checklist") || k === "confirm_possession");
  return (
    <div data-testid="asset-detail-page" className="fade-up">
      <Link to="/mezzi" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" />Mezzi</Link>
      <div className="card-soft mb-5 grid gap-5 p-5 sm:p-6 md:grid-cols-[1fr_auto]">
        <div>
          <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-lg font-bold">{a.code}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${ASSET_TONE[a.status]}`} data-testid="asset-status">{meta.statuses[a.status]}</span><DemoBadge show={a.is_demo} />
            {a.can.edit && <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="ml-auto h-9 rounded-xl" data-testid="asset-edit-button"><Pencil className="mr-1 h-4 w-4" />Modifica scheda</Button>}</div>
          <h1 className="mt-1 text-2xl font-extrabold">{a.brand} {a.model}</h1>
          <CoverPhoto a={a} onDone={setA} />
          <AssetGallery a={a} onDone={setA} />
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
            <p><span className="block text-xs text-muted-foreground">Categoria</span><b>{meta.categories[a.category]}</b></p>
            <p><span className="block text-xs text-muted-foreground">Matricola / targa</span><b>{a.serial || "—"}</b></p>
            <p><span className="block text-xs text-muted-foreground">Ubicazione</span><b>{a.location || "—"}</b></p>
            <p><span className="block text-xs text-muted-foreground">Assegnatario</span><b data-testid="asset-assignee">{a.assignee_name || "—"}</b></p>
            <p><span className="block text-xs text-muted-foreground">Ultimo controllo</span><b>{fmtDT(a.last_check_at)}</b></p>
            <p><span className="block text-xs text-muted-foreground">Prossima manutenzione</span><b className={a.maintenance_overdue ? "text-red-600" : ""}>{fmtD(a.next_maintenance_at)}</b></p>
            <p><span className="block text-xs text-muted-foreground">Scadenza revisione</span><b className={a.revision_due ? due30(a.revision_due) : ""} data-testid="asset-revision-due">{fmtD(a.revision_due)}</b></p>
            <p><span className="block text-xs text-muted-foreground">Scadenza assicurazione</span><b className={a.insurance_due ? due30(a.insurance_due) : ""} data-testid="asset-insurance-due">{fmtD(a.insurance_due)}</b></p>
            {a.required_qualification && <p><span className="block text-xs text-muted-foreground">Abilitazione richiesta</span><b>{a.required_qualification}</b></p>}
            {a.external_id_drflotta && <p><span className="block text-xs text-muted-foreground">ID DR FLOTTA</span><b>{a.external_id_drflotta}</b></p>}
            <p><span className="block text-xs text-muted-foreground">Commessa</span><b data-testid="asset-commessa">{a.commessa_code || "—"}</b></p>
          </div>
          {a.notes && <p className="mt-3 whitespace-pre-wrap rounded-xl bg-muted p-3 text-sm" data-testid="asset-notes">{a.notes}</p>}
        </div>
        <div className="text-center">
          <div ref={qr} className="inline-block rounded-xl border bg-white p-3" data-testid="asset-qr"><QRCodeSVG value={`${window.location.origin}/mezzi/${a.id}`} size={132} /></div>
          <Button variant="ghost" size="sm" onClick={printQr} className="mt-1 block w-full rounded-xl" data-testid="asset-qr-print"><Printer className="mr-1 inline h-4 w-4" />Stampa QR</Button>
        </div>
      </div>
      {a.status === "guasto" && <div className="mb-5 rounded-2xl bg-red-600 p-4 text-sm font-semibold text-white" data-testid="asset-blocked-banner">Mezzo non utilizzabile: nuove assegnazioni bloccate fino alla riabilitazione da parte della manutenzione.</div>}
      <div className="mb-6 space-y-2" data-testid="asset-actions">
        <div className="grid gap-2 sm:flex sm:flex-wrap">{primary.map((k) => <Button key={k} onClick={() => setAct(k)} className="min-h-[56px] rounded-xl px-6 text-base font-bold" data-testid={`asset-action-${k}`}>{ACTS[k]}</Button>)}</div>
        <div className="flex flex-wrap gap-2">{avail.filter((k) => !primary.includes(k)).map((k) => <Button key={k} variant={["retire", "report_fault"].includes(k) ? "destructive" : "outline"} onClick={() => setAct(k)} className="h-11 rounded-xl" data-testid={`asset-action-${k}`}>{ACTS[k]}</Button>)}</div>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <section className="card-soft p-5" data-testid="asset-faults"><h3 className="mb-3 font-bold">Guasti e danni ({a.faults.length})</h3>
          {a.faults.map((f) => (
            <div key={f.id} className={`mb-2 rounded-xl border-l-4 p-3 text-sm ${f.status === "aperto" ? "border-red-500 bg-red-50/60" : "border-emerald-500"}`}>
              <b>{f.blocking ? "Bloccante" : "Non bloccante"} · {f.status}</b> — {f.description}<p className="text-xs text-muted-foreground">{f.reported_by_name} · {fmtDT(f.at)}{f.resolution && ` · Risolto: ${f.resolution}`}</p>
              {f.status === "aperto" && a.can.resolve_fault && <Button size="sm" variant="outline" className="mt-2 rounded-xl" onClick={() => resolve(f)} data-testid={`fault-resolve-${f.id}`}>Segna risolto</Button>}
            </div>
          ))}
        </section>
        <section className="card-soft p-5" data-testid="asset-checklists"><h3 className="mb-3 font-bold">Registro controlli</h3>
          {a.checklists.map((c) => <p key={c.id} className="border-b py-2 text-sm last:border-0">{fmtDT(c.at)} · <b>{c.phase}</b> · {meta.end_outcomes[c.outcome]} · {c.by_name}{c.items.some((i) => !i.ok) && <span className="text-red-600"> · KO: {c.items.filter((i) => !i.ok).map((i) => i.label).join(", ")}</span>}</p>)}
          {a.maintenances.map((m) => <p key={m.id} className="border-b py-2 text-sm last:border-0">{fmtDT(m.at)} · <b>manutenzione</b> · {m.description} · {m.by_name}</p>)}
        </section>
        <section className="card-soft p-5" data-testid="asset-custody"><h3 className="mb-3 font-bold">Custodia e controlli possesso</h3>
          {a.custody.map((c) => <p key={c.id} className="border-b py-2 text-sm last:border-0">{fmtDT(c.at)} · <b>{c.action}</b> {c.user_name || ""} {c.note}</p>)}
          {a.possession_checks.map((p) => <p key={p.id} className="border-b py-2 text-sm last:border-0">Controllo possesso {fmtDT(p.requested_at)} · <b className={p.status === "in_attesa" ? "text-amber-700" : ""}>{p.status}</b> {p.state || ""} {p.note || ""}</p>)}
        </section>
        <section className="card-soft p-5" data-testid="asset-history"><h3 className="mb-3 flex items-center gap-2 font-bold"><History className="h-4 w-4 text-primary" />Storico</h3>
          {a.events.map((e) => <p key={e.id} className="border-b py-2 text-sm last:border-0"><b>{e.type.replaceAll("_", " ")}</b> {e.note} <span className="text-xs text-muted-foreground">{e.actor_name} · {fmtDT(e.at)}</span></p>)}
        </section>
      </div>
      {act && <ActDialog asset={a} act={act} meta={meta} onClose={() => setAct(null)} onDone={setA} />}
      <AssetFormDialog open={editing} onOpenChange={setEditing} meta={meta} asset={a} onSaved={setA} />
    </div>
  );
}
